// Социальное — tech/03 §3 (`respect`), §15 (друзья) и жалоба на имя или гараж (E06-T08; часть E10-T02 и E08-T02,
// вынесена раньше по заявке фронтенда, Р-59). Числа (10 уважений в сутки, 100 друзей) — конфиг `social` сервера.
import { z } from 'zod';
import { Grant, IsoTime, PublicId, strict } from '../common.js';
import { endpoint } from '../endpoint.js';

const PlayerParams = strict({ publicId: PublicId });

/** «Уважить» (ГД 14 §4, X-15): раз в сутки одному игроку, не больше 10 в сутки, получателю — от 2-го уровня. */
export const respectPlayer = endpoint({
  id: 'player.respect', method: 'POST', path: '/players/{publicId}/respect', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  params: PlayerParams, body: strict({}),
  response: strict({ given: z.literal(true), respectTotal: z.int().nonnegative(), leftToday: z.int().nonnegative() }), status: 200,
  errors: ['ALREADY_RESPECTED_TODAY', 'SELF', 'DAILY_LIMIT', 'REQUIREMENT_NOT_MET', 'NOT_FOUND'], spec: 'tech/03 §3; ГД 14 §4; X-15', task: 'E10-T02',
});

export const FriendEntry = strict({
  publicId: PublicId, displayName: z.string().min(1).max(32), level: z.int().min(1).max(100), since: IsoTime,
  // Заявка фронтенда-2 (E10-T04, Р-95): необязательны в схеме ради моков клиента; сервер отдаёт всегда.
  coopName: z.string().nullable().optional(),
  lastSeenAt: IsoTime.nullable().optional(),
  /** Уважение этому игроку сегодня уже оказано (ГД 14 §4: раз в сутки одному). */
  respectedToday: z.boolean().optional(),
});

export const getFriends = endpoint({
  id: 'friends.list', method: 'GET', path: '/friends', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: strict({ friends: z.array(FriendEntry), incoming: z.array(FriendEntry), outgoing: z.array(FriendEntry) }), status: 200,
  errors: [], spec: 'tech/03 §15; ГД 14 §2', task: 'E10-T02',
});

/** Заявка в друзья; встречная заявка уже есть — сразу дружба (`accepted`). */
export const addFriend = endpoint({
  id: 'friends.add', method: 'POST', path: '/friends/{publicId}', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: PlayerParams, body: strict({}), response: strict({ status: z.enum(['pending', 'accepted']) }), status: 200,
  errors: ['FRIENDS_LIMIT', 'SELF', 'NOT_FOUND'], spec: 'tech/03 §15; ГД 14 §2', task: 'E10-T02',
});

export const acceptFriend = endpoint({
  id: 'friends.accept', method: 'POST', path: '/friends/{publicId}/accept', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: PlayerParams, body: strict({}), response: strict({ status: z.literal('accepted') }), status: 200,
  errors: ['NOT_FOUND', 'FRIENDS_LIMIT'], spec: 'tech/03 §15', task: 'E10-T02',
});

export const removeFriend = endpoint({
  id: 'friends.remove', method: 'DELETE', path: '/friends/{publicId}', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: PlayerParams, response: strict({ ok: z.literal(true) }), status: 200, errors: ['NOT_FOUND'], spec: 'tech/03 §15', task: 'E10-T02',
});

/** Жалоба на имя или гараж игрока — в очередь модерации (E06-T08, E08-T02). Повтор в те же сутки — тот же `reportId`. */
export const reportPlayer = endpoint({
  id: 'report.create', method: 'POST', path: '/reports', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  body: strict({ target: z.enum(['name', 'garage']), publicId: PublicId, reason: z.enum(['offensive', 'spam', 'other']).optional() }),
  response: strict({ reportId: z.string().regex(/^[0-9]{1,19}$/), status: z.literal('queued') }), status: 201,
  errors: ['SELF', 'DAILY_LIMIT', 'NOT_FOUND'], spec: 'tech/03 §3 (жалобы, Р-59); E08-T02', task: 'E06-T08',
});

/** Рефералы (tech/03 §15, ГД 14 §3, economy/04 §7.7; E10-T02): ссылка, приглашённые и статус награды. */
export const getReferrals = endpoint({
  id: 'referrals.list', method: 'GET', path: '/referrals', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: strict({
    link: z.string().max(200),
    invited: z.array(strict({
      publicId: PublicId, level: z.int().min(1).max(100), status: z.enum(['linked', 'qualified_l3', 'qualified_l10', 'pending', 'capped', 'rejected_fraud']),
      displayName: z.string().min(1).max(32).optional(),
    })),
    rewardsLeft: z.int().nonnegative(),
    // Р-95: награда обоим за 3-й уровень приглашённого (конфиг `referral`) и награды месяца (предел ГД 14 §3).
    reward: Grant.optional(),
    rewardsMonth: strict({ used: z.int().nonnegative(), cap: z.int().positive() }).optional(),
  }), status: 200, errors: [], spec: 'tech/03 §15; ГД 14 §3', task: 'E10-T02',
});

/**
 * Подготовленное сообщение «Поделиться» (tech/03 §15, Т-К-66; заявка фронтенда-2 «share-message» — путь по tech/03, Р-95):
 * роль api просит роль bot вызвать `savePreparedInlineMessage` (Bot API 8.0), клиент открывает `shareMessage(id)`.
 * Кэш на игрока, вид и цель — 24 ч. 503 UNAVAILABLE — клиент откатывается на `t.me/share/url`.
 */
export const prepareShare = endpoint({
  id: 'share.prepare', method: 'POST', path: '/share/prepare', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  body: strict({ kind: z.enum(['referral', 'coop', 'boss', 'raid']), targetId: z.string().max(32).optional() }),
  response: strict({ preparedMessageId: z.string().min(1).max(128), expiresAt: IsoTime }), status: 200,
  errors: ['UNAVAILABLE', 'NOT_FOUND', 'NOT_IN_COOP'], spec: 'tech/03 §15; tech/14', task: 'E10-T04',
});
